import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  AppBar,
  Toolbar,
  Avatar,
  IconButton,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Chip,
  Fab,
  CircularProgress
} from '@mui/material';
import {
  Logout,
  ArrowBack,
  PhotoCamera,
  Download,
  Delete,
  Star,
  StarBorder,
  Close,
  Upload
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFamilyId } from '../contexts/FamilyIdContext';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';

function PhotoGalleryPage() {
  const { currentUser, userProfile, logout } = useAuth();
  const { familyId } = useFamilyId();
  const navigate = useNavigate();
  const [photos, setPhotos] = useState([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState({ open: false, photoId: null, storagePath: null });

  const isParent = userProfile?.role === 'parent';

  useEffect(() => {
    if (!currentUser || !familyId) {
      setPhotos([]);
      setLoading(false);
      return;
    }

    // Query photos based on role
    let photosQuery;
    if (isParent) {
      // Parents see all family photos
      photosQuery = query(
        collection(db, 'photos'),
        where('familyId', '==', familyId),
        orderBy('uploadedAt', 'desc')
      );
    } else {
      // Children see only their own photos
      photosQuery = query(
        collection(db, 'photos'),
        where('familyId', '==', familyId),
        where('userId', '==', currentUser.uid),
        orderBy('uploadedAt', 'desc')
      );
    }

    const unsubscribe = onSnapshot(photosQuery, (snapshot) => {
      const photosData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPhotos(photosData);
      setLoading(false);
    });

    return unsubscribe;
  }, [currentUser, familyId, isParent]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/auth');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      // Upload to Firebase Storage
      const storageRef = ref(storage, `photos/${familyId}/${currentUser.uid}/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const photoURL = await getDownloadURL(storageRef);

      // Save metadata to Firestore
      await addDoc(collection(db, 'photos'), {
        userId: currentUser.uid,
        familyId: familyId,
        type: 'gallery',
        url: photoURL,
        storagePath: storageRef.fullPath,
        uploadedAt: new Date().toISOString(),
        favorited: false,
        uploaderName: userProfile?.displayName || 'Unknown'
      });

      console.log('Photo uploaded successfully!');
    } catch (error) {
      console.error('Error uploading photo:', error);
      alert('Failed to upload photo. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleToggleFavorite = async (photoId, currentFavorite) => {
    if (!isParent) return; // Only parents can favorite
    try {
      await updateDoc(doc(db, 'photos', photoId), {
        favorited: !currentFavorite
      });
      // Update local state
      if (selectedPhoto && selectedPhoto.id === photoId) {
        setSelectedPhoto({ ...selectedPhoto, favorited: !currentFavorite });
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const handleDeletePhoto = async (photoId, storagePath) => {
    setConfirmDelete({ open: true, photoId, storagePath });
  };

  const executeDeletePhoto = async (photoId, storagePath) => {

    try {
      // Delete from Storage
      if (storagePath) {
        const storageRef = ref(storage, storagePath);
        await deleteObject(storageRef);
      }
      // Delete from Firestore
      await deleteDoc(doc(db, 'photos', photoId));
      setOpenDialog(false);
      console.log('Photo deleted successfully!');
    } catch (error) {
      console.error('Error deleting photo:', error);
    }
  };

  const handleDownloadPhoto = (url) => {
    window.open(url, '_blank');
  };

  const handleViewPhoto = (photo) => {
    setSelectedPhoto(photo);
    setOpenDialog(true);
  };

  // Filter photos by type
  const filterPhotos = (type) => {
    switch (type) {
      case 0: return photos; // All
      case 1: return photos.filter(p => p.type === 'task_before' || p.type === 'task_after'); // Chores
      case 2: return photos.filter(p => p.type === 'profile'); // Profile
      case 3: return photos.filter(p => p.favorited); // Favorites
      default: return photos;
    }
  };

  const filteredPhotos = filterPhotos(selectedTab);

  // Calculate storage used (rough estimate)
  const storageUsedMB = (photos.length * 0.5).toFixed(1); // Assume ~500KB per photo

  return (
    <Box sx={{ flexGrow: 1, minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* App Bar */}
      <AppBar position="static" sx={{ bgcolor: 'primary.main' }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" aria-label="back" onClick={() => navigate(-1)}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, ml: 2 }}>
            📸 Photo Gallery
          </Typography>
          <Avatar sx={{ bgcolor: 'secondary.main', mx: 2 }}>
            {userProfile?.displayName?.charAt(0)}
          </Avatar>
          <IconButton color="inherit" onClick={handleLogout}>
            <Logout />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography variant="h4" gutterBottom>
            {isParent ? 'Family Photo Gallery' : 'My Photo Gallery'}
          </Typography>
          <Typography variant="h6" color="text.secondary">
            {photos.length} photos • {storageUsedMB} MB used
          </Typography>
        </Box>

        {/* Filter Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={selectedTab} onChange={(e, newValue) => setSelectedTab(newValue)} centered>
            <Tab label="All" />
            <Tab label="Chores" />
            <Tab label="Profile" />
            <Tab label="Favorites" />
          </Tabs>
        </Box>

        {/* Photo Grid */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : filteredPhotos.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <PhotoCamera sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No photos yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Upload your first photo to get started!
            </Typography>
          </Box>
        ) : (
          <ImageList variant="masonry" cols={3} gap={12}>
            {filteredPhotos.map((photo) => (
              <ImageListItem key={photo.id} sx={{ cursor: 'pointer' }} onClick={() => handleViewPhoto(photo)}>
                <img
                  src={photo.url}
                  alt={`Task ${photo.taskTitle || photo.id}`}
                  loading="lazy"
                  style={{ borderRadius: 8 }}
                />
                <ImageListItemBar
                  title={photo.uploaderName}
                  subtitle={new Date(photo.uploadedAt).toLocaleDateString()}
                  actionIcon={
                    photo.favorited && (
                      <IconButton sx={{ color: 'warning.main' }}>
                        <Star />
                      </IconButton>
                    )
                  }
                />
              </ImageListItem>
            ))}
          </ImageList>
        )}

        {/* Upload Button */}
        <Fab
          color="primary"
          aria-label="upload photo"
          component="label"
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16
          }}
          disabled={uploading}
        >
          {uploading ? <CircularProgress size={24} /> : <Upload />}
          <input type="file" accept="image/*" hidden onChange={handlePhotoUpload} />
        </Fab>

        {/* Photo Detail Dialog */}
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
          {selectedPhoto && (
            <>
              <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="h6">Photo Details</Typography>
                  <IconButton onClick={() => setOpenDialog(false)}>
                    <Close />
                  </IconButton>
                </Box>
              </DialogTitle>
              <DialogContent>
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <img
                    src={selectedPhoto.url}
                    alt="Selected"
                    style={{ maxWidth: '100%', maxHeight: '60vh', borderRadius: 8 }}
                  />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="body1">
                    <strong>Uploaded by:</strong> {selectedPhoto.uploaderName}
                  </Typography>
                  <Typography variant="body1">
                    <strong>Date:</strong> {new Date(selectedPhoto.uploadedAt).toLocaleString()}
                  </Typography>
                  <Typography variant="body1">
                    <strong>Type:</strong>{' '}
                    <Chip
                      label={selectedPhoto.type.replace('_', ' ').toUpperCase()}
                      size="small"
                      color="primary"
                    />
                  </Typography>
                </Box>
              </DialogContent>
              <DialogActions>
                {isParent && (
                  <Button
                    startIcon={selectedPhoto.favorited ? <Star /> : <StarBorder />}
                    onClick={() => handleToggleFavorite(selectedPhoto.id, selectedPhoto.favorited)}
                    color={selectedPhoto.favorited ? 'warning' : 'default'}
                  >
                    {selectedPhoto.favorited ? 'Unfavorite' : 'Favorite'}
                  </Button>
                )}
                <Button startIcon={<Download />} onClick={() => handleDownloadPhoto(selectedPhoto.url)}>
                  Download
                </Button>
                {(isParent || selectedPhoto.userId === currentUser.uid) && (
                  <Button
                    startIcon={<Delete />}
                    onClick={() => handleDeletePhoto(selectedPhoto.id, selectedPhoto.storagePath)}
                    color="error"
                  >
                    Delete
                  </Button>
                )}
              </DialogActions>
            </>
          )}
        </Dialog>

        {/* Delete Photo Confirm Dialog */}
        <Dialog open={confirmDelete.open} onClose={() => setConfirmDelete({ open: false, photoId: null, storagePath: null })} maxWidth="xs" fullWidth>
          <DialogTitle>Delete Photo?</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to delete this photo? This cannot be undone.</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDelete({ open: false, photoId: null, storagePath: null })} sx={{ minHeight: 48 }}>Cancel</Button>
            <Button variant="contained" color="error" sx={{ minHeight: 48 }} onClick={() => {
              const { photoId, storagePath } = confirmDelete;
              setConfirmDelete({ open: false, photoId: null, storagePath: null });
              executeDeletePhoto(photoId, storagePath);
            }}>Delete</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
}

export default PhotoGalleryPage;

